import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {BeatLoader} from "react-spinners";
import {toast} from "react-toastify";
import Api from "../../Api/api";

/**
 * TokenAccess Component
 *
 * Handles token-based access to resources without requiring user login.
 * Primary use case: Allow external users to download generated documents via a shared link.
 *
 * URL format: /access/:token
 */
const TokenAccess = () => {
    const {token} = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (token) {
            validateAndAccess();
        } else {
            setError('Kein Token angegeben');
            setLoading(false);
        }
    }, [token]);

    const validateAndAccess = async () => {
        setLoading(true);
        setError(null);

        try {
            // First, validate the token
            const validateResponse = await Api().get(`/api/token/validate/${token}`);

            if (validateResponse.data.success) {
                setTokenInfo(validateResponse.data.data);

                // Auto-trigger download for document_generation resources
                if (validateResponse.data.data.resource_type === 'document_generation') {
                    await downloadResource();
                }
            } else {
                setError('Ungültiger oder abgelaufener Token');
            }
        } catch (err) {
            console.error('Token validation error:', err);
            if (err.response?.status === 401) {
                setError('Dieser Link ist ungültig, abgelaufen oder wurde bereits verwendet.');
            } else {
                setError('Fehler beim Überprüfen des Tokens');
            }
        } finally {
            setLoading(false);
        }
    };

    const downloadResource = async () => {
        setDownloading(true);

        try {
            // Access the resource with the token
            const response = await Api().get(`/api/token/access/${token}`, {
                responseType: 'blob' // Important for file download
            });

            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'document.pdf';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Dokument erfolgreich heruntergeladen');
        } catch (err) {
            console.error('Download error:', err);
            setError('Fehler beim Herunterladen der Datei');
            toast.error('Fehler beim Herunterladen der Datei');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className='min-h-screen flex flex-col justify-center items-center bg-gray-50'>
                <BeatLoader color="#1e3a8a" size={15} />
                <p className='mt-4 text-gray-600'>Token wird überprüft...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className='min-h-screen flex flex-col justify-center items-center bg-gray-50 px-4'>
                <div className='bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center'>
                    <div className='text-6xl mb-4'>⚠️</div>
                    <h2 className='text-2xl font-bold mb-4 text-gray-800'>Zugriff nicht möglich</h2>
                    <p className='text-gray-600 mb-6'>{error}</p>

                    <div className='bg-yellow-50 border border-yellow-200 rounded p-4 mb-6'>
                        <p className='text-sm text-yellow-800'>
                            <strong>Mögliche Gründe:</strong><br/>
                            • Der Link ist abgelaufen<br/>
                            • Der Link wurde bereits verwendet (bei Einmal-Links)<br/>
                            • Der Link ist ungültig
                        </p>
                    </div>

                    <button
                        onClick={() => navigate('/')}
                        className='bg-mainBlue text-white px-6 py-2 rounded hover:bg-blue-700 transition'
                    >
                        Zur Startseite
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='min-h-screen flex flex-col justify-center items-center bg-gray-50 px-4'>
            <div className='bg-white rounded-lg shadow-lg p-8 max-w-md w-full'>
                <div className='text-center mb-6'>
                    <div className='text-6xl mb-4'>✅</div>
                    <h2 className='text-2xl font-bold mb-2 text-gray-800'>Zugriff erfolgreich</h2>
                    <p className='text-gray-600'>Ihr Dokument wird heruntergeladen...</p>
                </div>

                {tokenInfo && (
                    <div className='bg-gray-50 rounded p-4 mb-6'>
                        <h3 className='font-semibold mb-2 text-gray-700'>Dokument-Informationen:</h3>
                        <div className='text-sm text-gray-600'>
                            {tokenInfo.metadata?.customer_name && (
                                <p><strong>Kunde:</strong> {tokenInfo.metadata.customer_name}</p>
                            )}
                            {tokenInfo.metadata?.document_name && (
                                <p><strong>Dokument:</strong> {tokenInfo.metadata.document_name}</p>
                            )}
                            {tokenInfo.expires_at && (
                                <p><strong>Link gültig bis:</strong> {new Date(tokenInfo.expires_at).toLocaleString('de-DE')}</p>
                            )}
                        </div>
                    </div>
                )}

                {downloading ? (
                    <div className='text-center'>
                        <BeatLoader color="#1e3a8a" size={10} />
                        <p className='mt-2 text-sm text-gray-600'>Download läuft...</p>
                    </div>
                ) : (
                    <div className='text-center'>
                        <button
                            onClick={downloadResource}
                            className='bg-mainBlue text-white px-6 py-2 rounded hover:bg-blue-700 transition mb-3'
                        >
                            Erneut herunterladen
                        </button>
                        <p className='text-xs text-gray-500'>
                            Falls der Download nicht automatisch gestartet wurde, klicken Sie auf die Schaltfläche oben.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TokenAccess;
